import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useAuth } from './AuthContext';
import App from './App';
import { postLogin } from './apiService';
import { jwtDecode } from 'jwt-decode';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaUser, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

  * { box-sizing: border-box; margin: 0; padding: 0; }

  .login-page {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    background: radial-gradient(ellipse at top left, #1a3a3a 0%, #0d2626 40%, #091e1e 100%);
    font-family: 'Inter', 'Segoe UI', sans-serif;
    overflow: hidden;
    position: relative;
  }

  /* Decorative blurred circles */
  .login-circle {
    position: absolute;
    border-radius: 50%;
    background: rgba(32, 80, 80, 0.45);
    filter: blur(0px);
    pointer-events: none;
  }
  .login-circle-tl {
    width: 220px;
    height: 220px;
    bottom: 30px;
    left: -60px;
  }
  .login-circle-tr {
    width: 200px;
    height: 200px;
    top: -40px;
    right: -50px;
  }
  .login-circle-br {
    width: 130px;
    height: 130px;
    bottom: 60px;
    right: 80px;
    background: rgba(32, 80, 80, 0.3);
  }

  /* Card */
  .login-card {
    position: relative;
    z-index: 10;
    width: 100%;
    max-width: 400px;
    padding: 44px 40px 40px;
    border-radius: 18px;
    background: rgba(20, 50, 50, 0.72);
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
    border: 1px solid rgba(255, 255, 255, 0.07);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5), 0 1px 0 rgba(255,255,255,0.06) inset;
    text-align: center;
  }

  /* Lock icon bubble */
  .login-icon-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 54px;
    height: 54px;
    border-radius: 14px;
    background: linear-gradient(135deg, #2dd4bf, #14b8a6);
    box-shadow: 0 6px 20px rgba(20, 184, 166, 0.45);
    margin-bottom: 18px;
  }
  .login-icon-wrap svg {
    font-size: 22px;
    color: #fff;
  }

  .login-title {
    font-size: 22px;
    font-weight: 700;
    letter-spacing: 2.5px;
    color: #e2f0ef;
    margin-bottom: 6px;
    text-transform: uppercase;
  }

  .login-subtitle {
    font-size: 13px;
    color: #7fa8a8;
    margin-bottom: 30px;
    font-weight: 400;
  }

  /* Field label */
  .login-label {
    display: block;
    text-align: left;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 1.4px;
    color: #7fa8a8;
    text-transform: uppercase;
    margin-bottom: 7px;
  }

  /* Input wrapper */
  .login-input-wrap {
    position: relative;
    margin-bottom: 20px;
  }

  .login-input {
    width: 100%;
    padding: 12px 14px;
    border-radius: 9px;
    border: 1px solid rgba(255,255,255,0.09);
    background: rgba(255,255,255,0.05);
    color: #d4eded;
    font-size: 14px;
    font-family: inherit;
    outline: none;
    transition: border-color 0.25s, box-shadow 0.25s;
  }
  .login-input::placeholder { color: #4d7a7a; }
  .login-input:focus {
    border-color: rgba(45, 212, 191, 0.5);
    box-shadow: 0 0 0 3px rgba(45, 212, 191, 0.12);
    background: rgba(255,255,255,0.07);
  }

  /* Eye toggle */
  .login-eye {
    position: absolute;
    top: 50%;
    right: 13px;
    transform: translateY(-50%);
    cursor: pointer;
    color: #4d7a7a;
    font-size: 14px;
    transition: color 0.2s;
  }
  .login-eye:hover { color: #2dd4bf; }

  /* Submit button */
  .login-btn {
    width: 100%;
    padding: 13px;
    border-radius: 9px;
    border: none;
    background: linear-gradient(90deg, #2dd4bf, #14b8a6);
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 2px;
    text-transform: uppercase;
    cursor: pointer;
    transition: opacity 0.25s, transform 0.15s, box-shadow 0.25s;
    box-shadow: 0 4px 18px rgba(20, 184, 166, 0.4);
    margin-top: 6px;
  }
  .login-btn:hover {
    opacity: 0.92;
    transform: translateY(-1px);
    box-shadow: 0 8px 24px rgba(20, 184, 166, 0.5);
  }
  .login-btn:active {
    transform: translateY(0);
    opacity: 1;
  }

  /* Fade-in animation */
  @keyframes loginFadeIn {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .login-card { animation: loginFadeIn 0.5s ease both; }
`;

const LoginForm = () => {
  const history = useHistory();
  const { isAuthenticated, login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const location = useLocation();

  const branchId = 1;
  const companyId = 1;

  const handleLogin = async () => {
    try {
      const jwtToken = await performLogin();
      const responseData = jwtDecode(jwtToken);
      const keysToRemove = [
        'iss', 'exp', 'ttp',
        'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name',
        'aud'
      ];
      const permissions = Object.keys(responseData).filter(
        (key) => key !== 'companyId' && key !== 'userId' && !keysToRemove.includes(key)
      );

      let isSuper = false;
      if (responseData.isSuperAdmin.toLowerCase() === "true") isSuper = true;

      if (!isSuper) {
        const parsedCompanyId = parseInt(responseData.companyId, 10);
        if (parsedCompanyId === companyId) {
          login(
            responseData.companyId, responseData.branchId, responseData.userId,
            isSuper, responseData.superAdminCompanyId, responseData.CompanyName,
            permissions, jwtToken, responseData.roleName,
            responseData.branchstate, responseData.username
          );
          const redirectParam = new URLSearchParams(location.search).get("redirect");
          history.replace(redirectParam ? decodeURIComponent(redirectParam) : "/Home");
        } else {
          toast.error('Invalid User!', { position: "top-center" });
        }
      } else {
        login(
          responseData.companyId, responseData.branchId, responseData.userId,
          isSuper, responseData.superAdminCompanyId, responseData.CompanyName,
          permissions, jwtToken, responseData.roleName,
          responseData.branchstate, responseData.username
        );
        const redirectParam = new URLSearchParams(location.search).get("redirect");
        history.replace(redirectParam ? decodeURIComponent(redirectParam) : "/Home");
      }
    } catch (error) {
      toast.error('Login failed', { position: "top-center" });
    }
  };

  const performLogin = async () => {
    const loginData = {
      UserName: username,
      Password: password,
      CompanyId: companyId,
      BranchId: branchId,
    };
    const response = await postLogin('LoginApi', loginData);
    if (response.responseCode === '1111') return response.response_data.token;
    throw new Error(response.Message);
  };

  if (isAuthenticated) return <App />;

  return (
    <>
      <style>{styles}</style>
      <ToastContainer />

      <div className="login-page">
        {/* Decorative circles */}
        <div className="login-circle login-circle-tl" />
        <div className="login-circle login-circle-tr" />
        <div className="login-circle login-circle-br" />

        <div className="login-card">
          {/* Lock icon */}
          <div className="login-icon-wrap">
            <FaLock />
          </div>

          <h2 className="login-title">Sign In</h2>
          <p className="login-subtitle">Enter your credentials to continue</p>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleLogin();
            }}
          >
            {/* Username */}
            <div className="login-input-wrap">
              <label className="login-label">Username</label>
              <input
                className="login-input"
                type="text"
                placeholder="Enter username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
              />
            </div>

            {/* Password */}
            <div className="login-input-wrap">
              <label className="login-label">Password</label>
              <input
                className="login-input"
                type={showPassword ? "text" : "password"}
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                style={{ paddingRight: "38px" }}
              />
              <span
                className="login-eye"
                onClick={() => setShowPassword((p) => !p)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>

            <button type="submit" className="login-btn">
              Continue
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default LoginForm;
